/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ValidSystemsDeadEndType } from 'types/dead-end';
import { isDeadEndReason } from 'utils/dead-end-reason';
import { sanitizeSystemOptionForPersist } from 'utils/sanitize-system-option';
import {
  AccessoryProductType,
  AirHandlerTypeCodeType,
  HyperHeatFilterValueType,
  SystemTypeFilterValueType,
  PartialWithRequired,
  SelectedDuctedSystemType,
  SelectedSystemType,
  SystemCombinationType,
  SystemOptionType,
  ValidSystemType
} from 'data/types';

// Shape of BulkValidSystemsResponse.filterUniverse: distinct Choose Systems
// filter values derived server-side from the UNFILTERED combination pool.
// TODO(AHA-6707): replace once @alpinehomeair/common publish lands
// (CombinationFilterUniverseType).
export type ServerFilterUniverseType = {
  airHandlerTypes: AirHandlerTypeCodeType[];
  condenserSeries: string[];
  condenserSeriesLabels: Record<string, string>;
  condenserBtus: number[];
  hyperHeat: HyperHeatFilterValueType[];
  systemTypes: SystemTypeFilterValueType[];
  // Verbatim refrigerant attribute strings (e.g. 'R-410A').
  refrigerants: string[];
};

// Shape of BulkValidSystemsResponse.filterOptionCounts: per-option what-if
// toggle counts derived server-side (combinations remaining if just that
// checkbox were flipped, everything else unchanged).
// TODO(AHA-6707): replace once @alpinehomeair/common publish lands
// (CombinationFilterOptionCounts).
export type ServerFilterOptionCountsType = {
  airHandlerTypes: Record<string, number>;
  condenserSeries: Record<string, number>;
  condenserBtus: Record<number, number>;
  hyperHeat: Record<string, number>;
  systemTypes: Record<string, number>;
  refrigerants: Record<string, number>;
};

export type QuotableSystemState = {
  accessoriesReviewed: boolean;
  isQuoted: boolean;
};

// Server-side pagination state for the "full" combinations gallery, keyed by
// configuration uniqueId. requestBodyHash ties loaded pages to the exact
// request body that produced them; load-more responses with a different hash
// are discarded (filters/config changed mid-flight).
export type CombinationPaginationState = {
  loadedPage: number;
  total: number;
  loadingMore: boolean;
  requestBodyHash: string;
};

export interface SystemsState {
  selectedSystems: SelectedSystemType[];
  selectedDuctedSystems: SelectedDuctedSystemType[];
  quotableSystems: Record<string, QuotableSystemState>;
  activeSelectedSystem: number;
  activeDuctedSystem: number;
  activeSystemType: 'ducted' | 'ductless';
  systemsForNewOption: ValidSystemType[];
  systemOptions: Record<string, SystemOptionType[]>;
  systemCombinationsValid: Record<string, SystemCombinationType[]>;
  systemCombinationsFull: Record<string, SystemCombinationType[]>;
  combinationPaginationByConfig: Record<string, CombinationPaginationState>;
  // Server-derived filter universe from the page-0 full response, keyed by
  // configuration uniqueId. Load-more responses are intentionally not
  // stored: page 0 always precedes them with an identical universe.
  filterUniverseByConfig: Record<string, ServerFilterUniverseType>;
  // Server-derived what-if option counts from the page-0 full response,
  // keyed by configuration uniqueId. Same contract as filterUniverseByConfig:
  // load-more responses never carry counts and never overwrite this.
  filterOptionCountsByConfig: Record<string, ServerFilterOptionCountsType>;
  // Why the latest page-0 valid-systems response produced no system the
  // customer can pick (response.deadEnd), keyed by configuration uniqueId.
  // Same contract as filterUniverseByConfig: load-more responses never carry
  // deadEnd and never overwrite it. Read by the Choose Systems list through
  // deadEndForConfigs (spec 001 T004).
  // TODO(page-0-meta): filterUniverseByConfig, filterOptionCountsByConfig
  // and deadEndByConfig share one contract (page-0 only, absent-means-
  // delete, zeroed in clearSystemCombinations, pruned in
  // removeStaleSystemCombinations) and want to be one page0MetaByConfig map
  // with one reducer. Deliberately not done in the PR that added the third
  // (#4803): the first two predate it and the collapse changes a persisted
  // shape (SNAPSHOT_SCHEMA_VERSION bump), which is its own change.
  deadEndByConfig: Record<string, ValidSystemsDeadEndType>;
  scenarioWinnersByConfig: Record<string, SystemCombinationType[]>;
  systemsToCompare: SystemOptionType[];
  comparisonRemovedUniqueIds: string[];
  // Bumped on every custom-system create/rename/delete so the valid-systems
  // watcher's request-body hash changes and the gallery refetches. The
  // server ignores the value; it only exists to bust the watcher's dedup.
  customSystemsRevisionByConfig: Record<string, number>;
  settledCustomSystemsRevisionByConfig: Record<string, number>;
  activeCustomSystemSavesByConfig: Record<string, number>;
  validSystemsRetryByConfig: Record<string, number>;
  pendingCompatibilityAddIds: number[];
}

export const defaultSystemsState: SystemsState = {
  selectedSystems: [],
  selectedDuctedSystems: [],
  quotableSystems: {},
  activeSelectedSystem: 0,
  activeDuctedSystem: 0,
  activeSystemType: 'ducted',
  systemsForNewOption: [],
  systemOptions: {},
  systemCombinationsValid: {},
  systemCombinationsFull: {},
  combinationPaginationByConfig: {},
  filterUniverseByConfig: {},
  filterOptionCountsByConfig: {},
  deadEndByConfig: {},
  scenarioWinnersByConfig: {},
  systemsToCompare: [],
  comparisonRemovedUniqueIds: [],
  customSystemsRevisionByConfig: {},
  settledCustomSystemsRevisionByConfig: {},
  activeCustomSystemSavesByConfig: {},
  validSystemsRetryByConfig: {},
  pendingCompatibilityAddIds: []
};

// Drops every entry whose key is outside keep. All page-0 by-config maps
// prune the same way in removeStaleSystemCombinations.
const pruneToKeepSet = <T>(
  record: Record<string, T>,
  keep: Set<string>
): void => {
  Object.keys(record).forEach((id) => {
    if (!keep.has(id)) delete record[id];
  });
};

const systemsSlice = createSlice({
  name: 'systems',
  initialState: defaultSystemsState,
  reducers: {
    setSelectedSystems: (
      state,
      action: PayloadAction<SelectedSystemType[]>
    ) => {
      state.selectedSystems = action.payload;
    },
    addSelectedSystem: (state, action: PayloadAction<SelectedSystemType>) => {
      if (state.selectedSystems.some((s) => s.id === action.payload.id)) {
        return;
      }
      state.selectedSystems.push(action.payload);
    },
    removeSelectedSystemById: (state, action: PayloadAction<string>) => {
      state.selectedSystems = state.selectedSystems.filter(
        (s) => s.id !== action.payload
      );
    },
    removeSelectedSystemsByKeys: (state, action: PayloadAction<string[]>) => {
      const keys = new Set(action.payload.filter(Boolean));
      state.selectedSystems = state.selectedSystems.filter(
        (system) => !keys.has(system.id) && !keys.has(system.uniqueId)
      );
      state.selectedDuctedSystems = state.selectedDuctedSystems.filter(
        (system) => !keys.has(String(system.id))
      );
    },
    noteComparisonRemovedUniqueIds: (
      state,
      action: PayloadAction<string[]>
    ) => {
      const next = new Set(state.comparisonRemovedUniqueIds ?? []);
      action.payload.forEach((id) => {
        if (id) next.add(id);
      });
      state.comparisonRemovedUniqueIds = Array.from(next);
    },
    clearComparisonRemovedUniqueIds: (state) => {
      state.comparisonRemovedUniqueIds = [];
    },
    updateSelectedSystem: (
      state,
      action: PayloadAction<PartialWithRequired<SelectedSystemType, 'uniqueId'>>
    ) => {
      state.selectedSystems = state.selectedSystems.map((system) =>
        system.uniqueId === action.payload.uniqueId
          ? { ...system, ...action.payload }
          : system
      );
    },
    setSelectedDuctedSystems: (
      state,
      action: PayloadAction<SelectedDuctedSystemType[]>
    ) => {
      state.selectedDuctedSystems = action.payload;
    },
    updateSelectedDuctedSystem: (
      state,
      action: PayloadAction<PartialWithRequired<SelectedDuctedSystemType, 'id'>>
    ) => {
      state.selectedDuctedSystems = state.selectedDuctedSystems.map((system) =>
        system.id === action.payload.id
          ? { ...system, ...action.payload }
          : system
      );
    },
    setActiveSelectedSystem: (state, action: PayloadAction<number>) => {
      state.activeSelectedSystem = action.payload;
    },
    setActiveDuctedSystem: (state, action: PayloadAction<number>) => {
      state.activeDuctedSystem = action.payload;
    },
    setActiveSystemType: (
      state,
      action: PayloadAction<'ducted' | 'ductless'>
    ) => {
      state.activeSystemType = action.payload;
    },
    setSystemsForNewOption: (
      state,
      action: PayloadAction<ValidSystemType[]>
    ) => {
      state.systemsForNewOption = action.payload;
    },
    setSystemOptions: (
      state,
      action: PayloadAction<Record<string, SystemOptionType[]>>
    ) => {
      state.systemOptions = action.payload;
    },
    setSystemOptionsForConfig: (
      state,
      action: PayloadAction<{
        configId: string;
        options: SystemOptionType[];
      }>
    ) => {
      state.systemOptions[action.payload.configId] = action.payload.options;
    },
    swapSystemOptions: (
      state,
      action: PayloadAction<{
        configId: string;
        fromIndex: number;
        toIndex: number;
      }>
    ) => {
      const { configId, fromIndex, toIndex } = action.payload;
      const options = state.systemOptions[configId];
      if (options) {
        const temp = options[fromIndex];
        options[fromIndex] = options[toIndex];
        options[toIndex] = temp;
      }
    },
    updateSystemOptionName: (
      state,
      action: PayloadAction<{
        configId: string;
        index: number;
        uniqueId: string;
        newName: string;
      }>
    ) => {
      const { configId, index, uniqueId, newName } = action.payload;
      if (state.systemOptions[configId]?.[index]) {
        state.systemOptions[configId][index].name = newName;
      }
      const compareEntry = state.systemsToCompare.find(
        (s) => s.uniqueId === uniqueId
      );
      if (compareEntry) {
        compareEntry.name = newName;
      }
      const validCombo = state.systemCombinationsValid[configId]?.find(
        (c) => c.uniqueId === uniqueId
      );
      if (validCombo) {
        validCombo.name = newName;
      }
      const fullCombo = state.systemCombinationsFull[configId]?.find(
        (c) => c.uniqueId === uniqueId
      );
      if (fullCombo) {
        fullCombo.name = newName;
      }
      const winnerCombo = state.scenarioWinnersByConfig[configId]?.find(
        (c) => c.uniqueId === uniqueId
      );
      if (winnerCombo) {
        winnerCombo.name = newName;
      }
    },
    deleteSystemOption: (
      state,
      action: PayloadAction<{ configId: string; optionId: string }>
    ) => {
      const { configId, optionId } = action.payload;
      if (state.systemOptions[configId]) {
        state.systemOptions[configId] = state.systemOptions[configId].filter(
          (option) => option.uniqueId !== optionId
        );
      }
    },
    setSystemCombinationsValid: (
      state,
      action: PayloadAction<{
        configId: string;
        combinations: SystemCombinationType[];
      }>
    ) => {
      state.systemCombinationsValid[action.payload.configId] =
        action.payload.combinations;
    },
    setSystemCombinationsFull: (
      state,
      action: PayloadAction<{
        configId: string;
        combinations: SystemCombinationType[];
      }>
    ) => {
      state.systemCombinationsFull[action.payload.configId] =
        action.payload.combinations;
    },
    setCombinationPagination: (
      state,
      action: PayloadAction<{
        configId: string;
        total: number;
        requestBodyHash: string;
      }>
    ) => {
      const { configId, total, requestBodyHash } = action.payload;
      state.combinationPaginationByConfig[configId] = {
        loadedPage: 0,
        total,
        loadingMore: false,
        requestBodyHash
      };
    },
    setCombinationLoadingMore: (
      state,
      action: PayloadAction<{ configId: string; loadingMore: boolean }>
    ) => {
      const { configId, loadingMore } = action.payload;
      const pagination = state.combinationPaginationByConfig[configId];
      if (!pagination) return;
      pagination.loadingMore = loadingMore;
    },
    appendSystemCombinationsFull: (
      state,
      action: PayloadAction<{
        configId: string;
        combinations: SystemCombinationType[];
        page: number;
        total: number;
        requestBodyHash: string;
      }>
    ) => {
      const { configId, combinations, page, total, requestBodyHash } =
        action.payload;
      const pagination = state.combinationPaginationByConfig[configId];
      // Stale-guard: only append pages produced by the same request body
      // that populated page 0. A mismatch means filters or the config
      // changed mid-flight; the watcher's page-0 refetch will replace
      // everything, so discard this response.
      if (!pagination || pagination.requestBodyHash !== requestBodyHash) {
        return;
      }
      const existing = state.systemCombinationsFull[configId] ?? [];
      const existingIds = new Set(existing.map((c) => c.uniqueId));
      const fresh = combinations.filter((c) => !existingIds.has(c.uniqueId));
      state.systemCombinationsFull[configId] = [...existing, ...fresh];
      pagination.loadedPage = page;
      pagination.total = total;
      pagination.loadingMore = false;
    },
    setFilterUniverseForConfig: (
      state,
      action: PayloadAction<{
        configId: string;
        filterUniverse?: ServerFilterUniverseType;
      }>
    ) => {
      const { configId, filterUniverse } = action.payload;
      if (filterUniverse) {
        state.filterUniverseByConfig[configId] = filterUniverse;
      } else {
        // A response without filterUniverse (older API build) must clear a
        // previously stored universe instead of leaving it stale.
        delete state.filterUniverseByConfig[configId];
      }
    },
    setFilterOptionCountsForConfig: (
      state,
      action: PayloadAction<{
        configId: string;
        filterOptionCounts?: ServerFilterOptionCountsType;
      }>
    ) => {
      const { configId, filterOptionCounts } = action.payload;
      if (filterOptionCounts) {
        state.filterOptionCountsByConfig[configId] = filterOptionCounts;
      } else {
        // A response without filterOptionCounts (older API build) must clear
        // previously stored counts instead of leaving them stale.
        delete state.filterOptionCountsByConfig[configId];
      }
    },
    setDeadEndForConfig: (
      state,
      action: PayloadAction<{
        configId: string;
        deadEnd?: ValidSystemsDeadEndType;
      }>
    ) => {
      const { configId, deadEnd } = action.payload;
      if (deadEnd) {
        // Normalize here — the one entry point into deadEndByConfig — so the
        // store always holds the closed reason union the type promises. The
        // response is cast, not parsed (valid-systems-watcher.tsx), so a code
        // from a newer API becomes 'unknown' with its other facts kept.
        const reason = isDeadEndReason(deadEnd.reason)
          ? deadEnd.reason
          : 'unknown';
        state.deadEndByConfig[configId] = { ...deadEnd, reason };
      } else {
        // A response without deadEnd (a build that produced systems, or an
        // older API build) must clear a previously stored diagnosis instead
        // of leaving it stale.
        delete state.deadEndByConfig[configId];
      }
    },
    setScenarioWinnersValid: (
      state,
      action: PayloadAction<{
        configId: string;
        scenarioWinners: SystemCombinationType[];
      }>
    ) => {
      state.scenarioWinnersByConfig[action.payload.configId] =
        action.payload.scenarioWinners;
    },
    clearSystemCombinations: (state) => {
      state.systemCombinationsValid = {};
      state.systemCombinationsFull = {};
      state.combinationPaginationByConfig = {};
      state.filterUniverseByConfig = {};
      state.filterOptionCountsByConfig = {};
      state.deadEndByConfig = {};
      state.scenarioWinnersByConfig = {};
    },
    removeStaleSystemCombinations: (state, action: PayloadAction<string[]>) => {
      const keep = new Set(action.payload);
      pruneToKeepSet(state.systemCombinationsValid, keep);
      pruneToKeepSet(state.systemCombinationsFull, keep);
      pruneToKeepSet(state.scenarioWinnersByConfig, keep);
      pruneToKeepSet(state.combinationPaginationByConfig, keep);
      pruneToKeepSet(state.filterUniverseByConfig, keep);
      pruneToKeepSet(state.filterOptionCountsByConfig, keep);
      pruneToKeepSet(state.deadEndByConfig, keep);
    },
    updateZoneSelectedLineSet: (
      state,
      action: PayloadAction<{
        activeSelectedSystem: number;
        zoneUniqueId: string;
        lineSetProduct?: AccessoryProductType;
      }>
    ) => {
      const { activeSelectedSystem, zoneUniqueId, lineSetProduct } =
        action.payload;
      const condenserIndex = state.selectedSystems[
        activeSelectedSystem
      ].configuration.condensers.findIndex((c) =>
        c.zones.some((z) => z.uniqueId === zoneUniqueId)
      );
      if (condenserIndex !== -1) {
        const zoneIndex = state.selectedSystems[
          activeSelectedSystem
        ].configuration.condensers[condenserIndex].zones.findIndex(
          (z) => z.uniqueId === zoneUniqueId
        );
        if (zoneIndex !== -1) {
          state.selectedSystems[activeSelectedSystem].configuration.condensers[
            condenserIndex
          ].zones[zoneIndex].selectedLineSet = lineSetProduct;
        }
      }
    },
    updateZoneAddWire: (
      state,
      action: PayloadAction<{
        activeSelectedSystem: number;
        zoneUniqueId: string;
        selected: boolean;
        firstWireClickZoneId: string | null;
      }>
    ) => {
      const {
        activeSelectedSystem,
        zoneUniqueId,
        selected,
        firstWireClickZoneId
      } = action.payload;
      const condenserIndex = state.selectedSystems[
        activeSelectedSystem
      ].configuration.condensers.findIndex((c) =>
        c.zones.some((z) => z.uniqueId === zoneUniqueId)
      );
      if (condenserIndex !== -1) {
        const zoneIndex = state.selectedSystems[
          activeSelectedSystem
        ].configuration.condensers[condenserIndex].zones.findIndex(
          (z) => z.uniqueId === zoneUniqueId
        );
        if (zoneIndex !== -1) {
          state.selectedSystems[activeSelectedSystem].configuration.condensers[
            condenserIndex
          ].zones[zoneIndex].addWire = selected;
        }
      }
      if (
        state.selectedSystems[
          activeSelectedSystem
        ].configuration.condensers.every((c) =>
          c.zones.every((z) => z.addWire)
        ) &&
        !selected &&
        firstWireClickZoneId === zoneUniqueId
      ) {
        state.selectedSystems[
          activeSelectedSystem
        ].configuration.condensers.forEach((c) => {
          c.zones.forEach((z) => {
            z.addWire = false;
          });
        });
      }
    },
    updateAllZonesAddWire: (
      state,
      action: PayloadAction<{
        activeSelectedSystem: number;
        addWire: boolean;
      }>
    ) => {
      const { activeSelectedSystem, addWire } = action.payload;
      state.selectedSystems[
        activeSelectedSystem
      ].configuration.condensers.forEach((c) => {
        c.zones.forEach((z) => {
          z.addWire = addWire;
        });
      });
    },
    updateDuctedZoneSelectedLineSet: (
      state,
      action: PayloadAction<{
        activeDuctedSystem: number;
        zoneUniqueId: string;
        lineSetProduct?: AccessoryProductType;
      }>
    ) => {
      const { activeDuctedSystem, zoneUniqueId, lineSetProduct } =
        action.payload;
      const condenserIndex = state.selectedDuctedSystems[
        activeDuctedSystem
      ].config.condensers.findIndex((c) =>
        c.zones.some((z) => z.uniqueId === zoneUniqueId)
      );
      if (condenserIndex !== -1) {
        const zoneIndex = state.selectedDuctedSystems[
          activeDuctedSystem
        ].config.condensers[condenserIndex].zones.findIndex(
          (z) => z.uniqueId === zoneUniqueId
        );
        if (zoneIndex !== -1) {
          state.selectedDuctedSystems[activeDuctedSystem].config.condensers[
            condenserIndex
          ].zones[zoneIndex].selectedLineSet = lineSetProduct;
        }
      }
    },
    updateDuctedZoneAddWire: (
      state,
      action: PayloadAction<{
        activeDuctedSystem: number;
        zoneUniqueId: string;
        selected: boolean;
        firstWireClickZoneId: string | null;
      }>
    ) => {
      const {
        activeDuctedSystem,
        zoneUniqueId,
        selected,
        firstWireClickZoneId
      } = action.payload;
      const condenserIndex = state.selectedDuctedSystems[
        activeDuctedSystem
      ].config.condensers.findIndex((c) =>
        c.zones.some((z) => z.uniqueId === zoneUniqueId)
      );
      if (condenserIndex !== -1) {
        const zoneIndex = state.selectedDuctedSystems[
          activeDuctedSystem
        ].config.condensers[condenserIndex].zones.findIndex(
          (z) => z.uniqueId === zoneUniqueId
        );
        if (zoneIndex !== -1) {
          state.selectedDuctedSystems[activeDuctedSystem].config.condensers[
            condenserIndex
          ].zones[zoneIndex].addWire = selected;
        }
      }
      if (
        state.selectedDuctedSystems[activeDuctedSystem].config.condensers.every(
          (c) => c.zones.every((z) => z.addWire)
        ) &&
        !selected &&
        firstWireClickZoneId === zoneUniqueId
      ) {
        state.selectedDuctedSystems[
          activeDuctedSystem
        ].config.condensers.forEach((c) => {
          c.zones.forEach((z) => {
            z.addWire = false;
          });
        });
      }
    },
    updateAllDuctedZonesAddWire: (
      state,
      action: PayloadAction<{
        activeDuctedSystem: number;
        addWire: boolean;
      }>
    ) => {
      const { activeDuctedSystem, addWire } = action.payload;
      state.selectedDuctedSystems[activeDuctedSystem].config.condensers.forEach(
        (c) => {
          c.zones.forEach((z) => {
            z.addWire = addWire;
          });
        }
      );
    },
    setSystemsToCompare: (state, action: PayloadAction<SystemOptionType[]>) => {
      state.systemsToCompare = action.payload.map(
        sanitizeSystemOptionForPersist
      );
    },
    addSystemToCompare: (state, action: PayloadAction<SystemOptionType>) => {
      if (state.systemsToCompare.some((s) => s.id === action.payload.id)) {
        return;
      }
      state.systemsToCompare.push(
        sanitizeSystemOptionForPersist(action.payload)
      );
    },
    removeSystemFromCompare: (state, action: PayloadAction<string>) => {
      state.systemsToCompare = state.systemsToCompare.filter(
        (s) => s.id !== action.payload
      );
    },
    setQuotableSystems: (
      state,
      action: PayloadAction<
        Array<{
          uniqueId: string;
          accessoriesReviewed: boolean;
          isQuoted: boolean;
        }>
      >
    ) => {
      state.quotableSystems = action.payload.reduce(
        (acc, { uniqueId, accessoriesReviewed, isQuoted }) => {
          acc[uniqueId] = { accessoriesReviewed, isQuoted };
          return acc;
        },
        {} as Record<string, QuotableSystemState>
      );
    },
    setSystemAccessoriesReviewed: (
      state,
      action: PayloadAction<{ uniqueId: string; accessoriesReviewed: boolean }>
    ) => {
      const { uniqueId, accessoriesReviewed } = action.payload;
      state.quotableSystems[uniqueId] = {
        ...state.quotableSystems[uniqueId],
        accessoriesReviewed
      };
    },
    setSystemIsQuoted: (
      state,
      action: PayloadAction<{ uniqueId: string; isQuoted: boolean }>
    ) => {
      const { uniqueId, isQuoted } = action.payload;
      state.quotableSystems[uniqueId] = {
        ...state.quotableSystems[uniqueId],
        isQuoted
      };
    },
    bumpCustomSystemsRevision: (
      state,
      action: PayloadAction<{ configId: string }>
    ) => {
      const { configId } = action.payload;
      const prev = state.customSystemsRevisionByConfig[configId] ?? 0;
      state.customSystemsRevisionByConfig[configId] = prev + 1;
    },
    settleCustomSystemsRevision: (
      state,
      action: PayloadAction<{ configId: string; revision: number }>
    ) => {
      const { configId, revision } = action.payload;
      const prev = state.settledCustomSystemsRevisionByConfig[configId] ?? 0;
      state.settledCustomSystemsRevisionByConfig[configId] = Math.max(
        prev,
        revision
      );
    },
    beginCustomSystemSave: (
      state,
      action: PayloadAction<{ configId: string }>
    ) => {
      const { configId } = action.payload;
      const prev = state.activeCustomSystemSavesByConfig[configId] ?? 0;
      state.activeCustomSystemSavesByConfig[configId] = prev + 1;
    },
    endCustomSystemSave: (
      state,
      action: PayloadAction<{ configId: string }>
    ) => {
      const { configId } = action.payload;
      const prev = state.activeCustomSystemSavesByConfig[configId] ?? 0;
      const next = Math.max(0, prev - 1);
      if (next === 0) {
        delete state.activeCustomSystemSavesByConfig[configId];
      } else {
        state.activeCustomSystemSavesByConfig[configId] = next;
      }
    },
    bumpValidSystemsRetry: (
      state,
      action: PayloadAction<{ configId: string }>
    ) => {
      const { configId } = action.payload;
      const prev = state.validSystemsRetryByConfig[configId] ?? 0;
      state.validSystemsRetryByConfig[configId] = prev + 1;
    },
    beginCompatibilityAdds: (state, action: PayloadAction<number[]>) => {
      action.payload.forEach((combinationId) => {
        if (!state.pendingCompatibilityAddIds.includes(combinationId)) {
          state.pendingCompatibilityAddIds.push(combinationId);
        }
      });
    },
    endCompatibilityAdds: (state, action: PayloadAction<number[]>) => {
      const ended = new Set(action.payload);
      const hasPending = state.pendingCompatibilityAddIds.some(
        (combinationId) => ended.has(combinationId)
      );
      if (!hasPending) return;
      state.pendingCompatibilityAddIds =
        state.pendingCompatibilityAddIds.filter(
          (combinationId) => !ended.has(combinationId)
        );
    }
  }
});

export const {
  setSelectedSystems,
  addSelectedSystem,
  removeSelectedSystemById,
  removeSelectedSystemsByKeys,
  noteComparisonRemovedUniqueIds,
  clearComparisonRemovedUniqueIds,
  updateSelectedSystem,
  setSelectedDuctedSystems,
  updateSelectedDuctedSystem,
  setActiveSelectedSystem,
  setActiveDuctedSystem,
  setActiveSystemType,
  setSystemsForNewOption,
  setSystemOptions,
  setSystemOptionsForConfig,
  swapSystemOptions,
  updateSystemOptionName,
  deleteSystemOption,
  setSystemCombinationsValid,
  setSystemCombinationsFull,
  setCombinationPagination,
  setCombinationLoadingMore,
  appendSystemCombinationsFull,
  setFilterUniverseForConfig,
  setFilterOptionCountsForConfig,
  setDeadEndForConfig,
  setScenarioWinnersValid,
  clearSystemCombinations,
  removeStaleSystemCombinations,
  updateZoneSelectedLineSet,
  updateZoneAddWire,
  updateAllZonesAddWire,
  updateDuctedZoneSelectedLineSet,
  updateDuctedZoneAddWire,
  updateAllDuctedZonesAddWire,
  setSystemsToCompare,
  addSystemToCompare,
  removeSystemFromCompare,
  setQuotableSystems,
  setSystemAccessoriesReviewed,
  setSystemIsQuoted,
  bumpCustomSystemsRevision,
  settleCustomSystemsRevision,
  beginCustomSystemSave,
  endCustomSystemSave,
  bumpValidSystemsRetry,
  beginCompatibilityAdds,
  endCompatibilityAdds
} = systemsSlice.actions;

export default systemsSlice.reducer;
